import React, { useCallback, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { LeadsTable } from './components/LeadsTable'
import CampaignMetrics from './components/CampaignMetrics'
import { useParams, useSearchParams } from 'react-router-dom'
import CampaignSetup from '../../components/CampaignSetup/CampaignSetup'
import useFetchLeads from '../../api/lead/useFetchLeads'
import LeadDetail from './components/LeadDetail'
import ModalWithScroll from '../../components/ModalWithScroll/ModalWithScroll'
import DetailsHeader from './components/DetailsHeader/DetailsHeader'
import { BreadCrumb } from '../../components/Breadcrumb/BreadCrumb'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import ModalWithSidebarProvider, { useShowSidebar } from '../../components/ModalWithSidebar/ModalWithSidebar'
import CallProspects from './components/CallProspects/CallProspects'
import useActionFetchCampaign from '../../store/hooks/campaigns/useActionFetchCampaign'

function CampaignSetupWrapper() {
	const { openSidebar } = useShowSidebar()
	return <CampaignSetup openSidebar={openSidebar} />
}

export default function CampaignDetailsPage() {
	const { campaignId } = useParams()
	const [searchParams, setSearchParams] = useSearchParams()

	const [leadDetailId, setLeadDetailId] = useState(searchParams.get('lead_id'))
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

	const { campaignDetails } = useActionFetchCampaign(campaignId, showConfirmationDialog, 5000)

	const { leads, fetchingLeads, pendingLeads, loadMore } = useFetchLeads(
		campaignId,
		searchParams.get('limit'),
		searchParams,
	)

	const handleRowClick = (leadId) => {
		setLeadDetailId(leadId)
		appendSearchParams({ lead_id: leadId })
	}

	const onCloseLeadDetails = () => {
		setLeadDetailId(null)
		searchParams.delete('lead_id')
		setSearchParams(searchParams)
	}

	const closeDialog = useCallback(() => {
		setShowConfirmationDialog(false)
	}, [])

	const openDialog = useCallback(() => setShowConfirmationDialog(true), [])

	const disabledCampaignDialog = useMemo(() => leads?.length === 0 || campaignId === 'null', [campaignId, leads])

	const appendSearchParams = useCallback(
		(newParams) => {
			if (Object.keys(newParams).length) {
				const params = {
					...Object.fromEntries(searchParams),
					...newParams,
				}
				setSearchParams(new URLSearchParams(params))
			} else {
				setSearchParams(new URLSearchParams())
			}
		},
		[searchParams],
	)

	const filterLeads = useCallback(
		(params) => {
			appendSearchParams(params)
		},
		[appendSearchParams],
	)

	const onLoadMore = useCallback(() => {
		const defaultPaging = process.env.REACT_APP_DEFAULT_PAGING
		const limit = +(searchParams.get('limit') || defaultPaging) + +defaultPaging
		appendSearchParams({ limit })
	}, [searchParams])

	return (
		<React.Fragment>
			<ContentLayout
				BreadCrumbsTrail={<BreadCrumb pathname="/details" location={campaignDetails?.campaign_name} />}
			>
				<Box flex="column" width="100%">
					<DetailsHeader
						openCampaignDialog={openDialog}
						disabled={disabledCampaignDialog}
						campaign={campaignDetails}
					/>

					<CampaignMetrics campaign={campaignDetails} filterAction={filterLeads} />

					<LeadsTable
						leads={leads}
						isLoading={pendingLeads}
						handleRowClick={handleRowClick}
						enableLoadMore={loadMore}
						onLoadMore={onLoadMore}
					/>
				</Box>
			</ContentLayout>

			<ModalWithScroll show={!!leadDetailId} onClose={onCloseLeadDetails}>
				<LeadDetail leadId={leadDetailId} dismiss={onCloseLeadDetails} />
			</ModalWithScroll>

			<ModalWithSidebarProvider show={showConfirmationDialog}>
				<CallProspects onClose={closeDialog} campaign={campaignDetails}>
					<CampaignSetupWrapper />
				</CallProspects>
			</ModalWithSidebarProvider>
		</React.Fragment>
	)
}
